import { teamsClient } from '@atlaskit/teams-client';
import { type TeamAgentAssociation } from '@atlaskit/teams-client/types';

import { logErrorMessage } from '../../../../common/utils/sentry';
import { type StoreApi } from '../../types';

import { addAgentState } from './utils';

export const addAgentsToTeam =
	(
		teamId: string,
		agents: {
			id: string;
			avatarUrl?: string;
			name: string;
		}[],
	) =>
	async ({ getState, setState }: StoreApi) => {
		const agentIds = agents.map((agent) => agent.id);

		const addAgentsResponse = await teamsClient.addAgentsToTeam(teamId, agentIds);

		const associations = addAgentsResponse.agents.reduce<Required<TeamAgentAssociation>[]>(
			(acc, agentId) => {
				if (!agentId) {
					logErrorMessage('teamAgentAssociationInvalidResponse', {
						message: 'Agent association in response was invalid',
						agent: agentId,
						response: addAgentsResponse,
					});

					return acc;
				}

				const agent = agents.find((agent) => agent.id === agentId);

				if (!agent) {
					logErrorMessage('teamAgentAssociationInvalidResponse', {
						message: 'Could not find agent details for this association',
						agent: agentId,
						response: addAgentsResponse,
					});

					return acc;
				}

				acc.push({
					associationId: {
						accountId: agentId,
						teamId: teamId,
					},
					agent: {
						id: agent.id,
						avatarUrl: agent.avatarUrl,
						fullName: agent.name,
					},
				});

				return acc;
			},
			[],
		);

		const state = getState();

		// If focused team has changed whilst invite request was resolving, abort.
		if (teamId !== state.teamAgents.teamId) {
			return;
		}

		const nextState = addAgentState(state, associations);
		setState(nextState);

		return agents;
	};
